import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

export enum ConnectionStatus {
    Connecting,
    Connected,
    Disconnected,
    Reconnecting
}

/**
 * Interface para estandarizar los listeners de mensajes.
 * Ayuda a gestionar y remover listeners de forma segura.
 */
interface MessageListener {
    id: number;
    callback: (data: any) => void;
}

@Injectable({
    providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
    private readonly WS_URL = (environment as any).wsUrl;
    private socket: WebSocket | null = null;
    private listeners: MessageListener[] = [];
    private listenerIdCounter = 0;
    private connectionStatus$ = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.Disconnected);

    // --- Parámetros para la reconexión automática ---
    private reconnectInterval = 1000; // Inicia en 1 segundo
    private maxReconnectInterval = 30000; // Máximo 30 segundos
    private reconnectTimeoutId: any;

    constructor() {
        this.connect();
    }

    public getStatus(): Observable<ConnectionStatus> {
        return this.connectionStatus$.asObservable();
    }

    public connect(): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            return;
        }

        this.reconnectInterval = 1000;
        this.connectionStatus$.next(ConnectionStatus.Connecting);
        console.log(`Intentando conectar a ${this.WS_URL}...`);
        this.socket = new WebSocket(this.WS_URL);

        this.socket.onopen = (event) => this.handleOpen(event);
        this.socket.onmessage = (event) => this.handleMessage(event);
        this.socket.onclose = (event) => this.handleClose(event);
        this.socket.onerror = (event) => this.handleError(event);
    }

    /**
     * Manejador para el evento 'onopen'.
     * Se ejecuta cuando la conexión se establece correctamente.
     */
    private handleOpen(event: Event): void {
        this.connectionStatus$.next(ConnectionStatus.Connected);
        console.log('Conexión WebSocket establecida.');
        // Al conectar exitosamente, reseteamos el intervalo de reconexión.
        this.reconnectInterval = 1000;
        // Si había un intento de reconexión programado, lo cancelamos.
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }
    }

    /**
     * Manejador para el evento 'onmessage'.
     * Procesa los mensajes recibidos del servidor y los notifica a los listeners.
     */
    private handleMessage(event: MessageEvent): void {
        try {
            const data = JSON.parse(event.data);
            console.log('Mensaje recibido:', data);
            // Notifica a todos los componentes que están escuchando
            this.listeners.forEach(listener => listener.callback(data));
        } catch (error) {
            console.error('Error al parsear el mensaje JSON:', event.data, error);
            // Si el mensaje no es JSON, lo pasamos tal cual
            this.listeners.forEach(listener => listener.callback(event.data));
        }
    }

    /**
     * Manejador para el evento 'onclose'.
     * Se ejecuta cuando la conexión se cierra. Inicia el proceso de reconexión.
     */
    private handleClose(event: CloseEvent): void {
        console.warn(`Conexión WebSocket cerrada. Código: ${event.code}. Razón: ${event.reason}`);
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
        // No intentar reconectar si el cierre fue normal (código 1000)
        if (event.code !== 1000 && this.listeners.length > 0) {
            this.scheduleReconnect();
        }
    }

    /**
     * Manejador para el evento 'onerror'.
     * Se ejecuta cuando ocurre un error en la conexión.
     */
    private handleError(event: Event): void {
        console.error('Error en WebSocket:', event);
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
    }

    /**
     * Programa el siguiente intento de reconexión usando una estrategia
     * de "exponential backoff" para no sobrecargar el servidor.
     */
    private scheduleReconnect(): void {
        this.connectionStatus$.next(ConnectionStatus.Reconnecting);
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }

        console.log(`Intentando reconectar en ${this.reconnectInterval / 1000} segundos...`);

        this.reconnectTimeoutId = setTimeout(() => {
            this.connect();
            // Duplicamos el intervalo para el siguiente intento, hasta un máximo.
            this.reconnectInterval = Math.min(this.maxReconnectInterval, this.reconnectInterval * 2);
        }, this.reconnectInterval);
    }

    /**
     * Envía datos al servidor WebSocket.
     * @param data Los datos a enviar. Se convertirán a formato JSON.
     */
    public sendMessage(data: any): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('Enviando mensaje:', data);
            this.socket.send(JSON.stringify(data));
        } else {
            console.error('No se puede enviar el mensaje. El WebSocket no está conectado.');
        }
    }

    /**
     * Registra una función callback para que sea llamada cuando se reciba un mensaje.
     * @param callback La función que manejará los datos recibidos.
     * @returns El ID del listener, para poder removerlo después.
     */
    public addMessageListener(callback: (data: any) => void): number {
        this.listenerIdCounter++;
        this.listeners.push({ id: this.listenerIdCounter, callback });
        return this.listenerIdCounter;
    }

    /**
     * Elimina un listener de mensajes usando su ID.
     * @param listenerId El ID retornado por `addMessageListener`.
     */
    public removeMessageListener(listenerId: number): void {
        this.listeners = this.listeners.filter(listener => listener.id !== listenerId);
        console.log(`Listener con ID ${listenerId} removido. Total: ${this.listeners.length}`);
    }

    /**
     * Cierra la conexión WebSocket de forma manual y limpia los recursos.
     */
    public closeConnection(): void {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }
        if (this.socket) {
            // Usamos el código 1000 para indicar un cierre normal y evitar la reconexión.
            this.socket.close(1000, 'Cierre manual por el cliente');
            this.socket = null;
        }
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
    }

    ngOnDestroy(): void {
        this.closeConnection();
        this.connectionStatus$.complete();
    }
}

